/**
 * About / credits. Reachable from the title screen and the topbar.
 *
 * Dismissable, unlike the draft: nothing here is a decision.
 */
import { TID } from '../testids.ts';
import { btn, el, on } from './dom.ts';
import { Modal } from './modal.ts';
import type { UICtx } from './types.ts';

export const KOFI_URL = 'https://ko-fi.com/maxnovich';
/** Source. Linked from the menu so players can read it, and file bugs. */
export const REPO_URL = 'https://github.com/Kvadratni/tokenmaxxing-2';
/** The first game, where you were the human. */
export const PREQUEL_URL = 'https://kvadratni.github.io/tokenmaxxing/';

interface CreditLine {
  readonly what: string;
  readonly how: string;
}

const CREDITS: readonly CreditLine[] = [
  { what: 'Code', how: 'Vanilla TypeScript. No framework, no game engine.' },
  { what: 'Pixel art', how: 'Generated by a committed script: npm run art redraws every sprite.' },
  { what: 'Type', how: 'A 5x7 bitmap font, compiled into a TrueType file in the browser at boot.' },
  { what: 'Audio', how: 'Synthesised live with WebAudio. There are no sound files.' },
  { what: 'Dependencies', how: 'None at runtime. The whole game is one bundle.' },
];

/** Opens a link in a new tab without handing it a window reference. */
export function extLink(
  parent: HTMLElement,
  href: string,
  text: string,
  cls: string,
  testid?: string,
): HTMLAnchorElement {
  const a = el('a', { cls, text, parent });
  a.setAttribute('href', href);
  a.setAttribute('target', '_blank');
  a.setAttribute('rel', 'noopener noreferrer');
  if (testid) a.setAttribute('data-testid', testid);
  return a;
}

export class About {
  readonly modal: Modal;
  private readonly disposers: Array<() => void> = [];

  constructor(parent: HTMLElement, _ctx: UICtx) {
    this.modal = new Modal({
      tid: TID.aboutModal,
      label: 'About Tokenmaxxing 2',
      dismissable: true,
      cls: 'tm-about',
      // `Modal` reports the dismissal; closing is the owner's job.
      onDismiss: () => this.close(),
    });
    parent.appendChild(this.modal.el);

    el('h2', { cls: 'tm-modal__title', text: 'About', parent: this.modal.panel });
    el('p', {
      cls: 'tm-modal__sub',
      text:
        'Tokenmaxxing 2: You’re Absolutely Right. A roguelike idle clicker where you are the ' +
        'coding agent. Every token you generate fills your context window, and the human on the ' +
        'other side of the glass is running out of patience.',
      parent: this.modal.panel,
    });
    el('p', {
      cls: 'tm-modal__sub',
      text: 'In the first game you were the human, hiring agents. It is still playable, and the sequel can tell if you did.',
      parent: this.modal.panel,
    });

    const list = el('dl', { cls: 'tm-about__credits', parent: this.modal.panel });
    for (const c of CREDITS) {
      el('dt', { text: c.what, parent: list });
      el('dd', { text: c.how, parent: list });
    }

    const foot = el('div', { cls: 'tm-about__foot', parent: this.modal.panel });
    el('p', {
      cls: 'tm-about__thanks',
      text: 'Free, and staying that way. If it made you smile, you can buy me a coffee.',
      parent: foot,
    });
    extLink(foot, REPO_URL, 'Source on GitHub', 'tm-btn tm-btn--quiet', TID.repoLink);
    extLink(foot, PREQUEL_URL, 'Tokenmaxxing 1', 'tm-btn tm-btn--quiet', TID.prequelLink);
    extLink(foot, KOFI_URL, 'Buy me a coffee', 'tm-btn tm-btn--kofi', TID.kofiLink);

    const close = btn({ cls: 'tm-btn', tid: TID.aboutClose, text: 'Close', parent: foot });
    this.disposers.push(on(close, 'click', () => this.close()));
  }

  get isOpen(): boolean {
    return this.modal.isOpen;
  }

  open(): void {
    this.modal.open(null);
  }

  close(): void {
    this.modal.close();
  }

  toggle(): void {
    if (this.modal.isOpen) this.close();
    else this.open();
  }

  destroy(): void {
    for (const d of this.disposers) d();
    this.disposers.length = 0;
    this.modal.destroy();
  }
}
